import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from '../../i18n/LanguageContext';
import { Home, MessageCircle, Send, Search, ChevronRight, ArrowLeft, Smile, Users, Paperclip, Pencil, LogOut, Download, Eye, FileText, SmilePlus } from 'lucide-react';
import { API_BASE } from '../../config';
import { io } from 'socket.io-client';
import EmojiPicker from 'emoji-picker-react';

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Authorization': token ? `Bearer ${token}` : '',
    'Content-Type': 'application/json'
  };
};

// Server URL from env (API_BASE) — for file preview (static) and download (API with auth)
const getAttachmentUrl = (path) => {
  if (!path) return '';
  const base = (typeof API_BASE === 'string' ? API_BASE : '').replace(/\/$/, '');
  const p = String(path).replace(/^\//, '');
  return p ? `${base}/uploads/${p}` : '';
};

const getDownloadUrl = (path, name, disposition = 'attachment') => {
  if (!path) return '';
  const base = (typeof API_BASE === 'string' ? API_BASE : '').replace(/\/$/, '');
  const p = String(path).replace(/^\//, '');
  if (!p) return '';
  const params = new URLSearchParams({ path: p });
  if (name) params.set('name', name);
  if (disposition) params.set('disposition', disposition);
  return `${base}/api/chat/download?${params.toString()}`;
};

const CHAT_FILE_SIZE_LIMIT = 15 * 1024 * 1024; // 15MB
const IMAGE_EXT = /\.(jpe?g|png|gif|webp|bmp)$/i;
const PDF_EXT = /\.pdf$/i;
const isImageFileName = (name) => name && IMAGE_EXT.test(name);
const isPdfFileName = (name) => name && PDF_EXT.test(name);

const QUICK_REACTIONS = ['👍', '❤️', '😄', '😮', '😢', '🔥'];

/** Image that loads via authenticated API so view works in live env (no static /uploads/). */
function ChatAttachmentImage({ path, name, alt }) {
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);
  const blobUrlRef = useRef(null);
  useEffect(() => {
    if (!path) return;
    const url = getDownloadUrl(path, name, 'inline');
    fetch(url, { headers: getAuthHeaders() })
      .then(r => { if (!r.ok) throw new Error(); return r.blob(); })
      .then(blob => {
        if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = URL.createObjectURL(blob);
        setSrc(blobUrlRef.current);
      })
      .catch(() => setFailed(true));
    return () => { if (blobUrlRef.current) { URL.revokeObjectURL(blobUrlRef.current); blobUrlRef.current = null; } setSrc(null); };
  }, [path, name]);
  if (failed || !src) return <span style={{ fontSize: '13px', color: 'rgba(255,255,255,0.6)' }}>{name}</span>;
  return <img src={src} alt={alt || name} style={{ maxWidth: '100%', maxHeight: 280, borderRadius: '12px', objectFit: 'contain', border: '1px solid rgba(255,255,255,0.2)', cursor: 'pointer' }} />;
}

/**
 * Live Chat module (Enterprise plan).
 * Company-specific: only employees from the same company. Professional UI with contacts + conversations.
 */
function LiveChat({ onNavigate }) {
  const { t } = useTranslation();
  const [companyUsers, setCompanyUsers] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [loadingConversations, setLoadingConversations] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [searchContact, setSearchContact] = useState('');
  const [socket, setSocket] = useState(null);
  const [typingUser, setTypingUser] = useState(null);
  const [narrowScreen, setNarrowScreen] = useState(typeof window !== 'undefined' && window.innerWidth < 768);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [groupMemberIds, setGroupMemberIds] = useState([]);
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [chatSearchQuery, setChatSearchQuery] = useState('');
  const [chatSearchResults, setChatSearchResults] = useState([]);
  const [showChatSearch, setShowChatSearch] = useState(false);
  const [showEditGroupModal, setShowEditGroupModal] = useState(false);
  const [editGroupName, setEditGroupName] = useState('');
  const [editGroupParticipants, setEditGroupParticipants] = useState([]);
  const [editGroupParticipantIds, setEditGroupParticipantIds] = useState([]);
  const [showViewMembersModal, setShowViewMembersModal] = useState(false);
  const [viewMembersList, setViewMembersList] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [fileError, setFileError] = useState('');
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [onlineUserIds, setOnlineUserIds] = useState([]);
  const [reactionPickerMessageId, setReactionPickerMessageId] = useState(null);
  const [reactionFullPickerMessageId, setReactionFullPickerMessageId] = useState(null);
  const messagesEndRef = useRef(null);
  const messagesTopRef = useRef(null);
  const messagesScrollRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const messageRefsMap = useRef({});
  const selectedConversationIdRef = useRef(selectedConversationId);
  const conversationsRef = useRef(conversations);
  selectedConversationIdRef.current = selectedConversationId;
  conversationsRef.current = conversations;

  useEffect(() => {
    const onResize = () => setNarrowScreen(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Close reaction pickers when clicking outside (any side)
  useEffect(() => {
    const handleMouseDown = (e) => {
      if (!reactionPickerMessageId && !reactionFullPickerMessageId) return;
      if (e.target.closest('[data-reaction-picker]') || e.target.closest('[data-reaction-trigger]')) return;
      setReactionPickerMessageId(null);
      setReactionFullPickerMessageId(null);
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [reactionPickerMessageId, reactionFullPickerMessageId]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    if (!fileError) return;
    const t = setTimeout(() => setFileError(''), 5000);
    return () => clearTimeout(t);
  }, [fileError]);

  // Initial load: fetch company users and conversations in parallel
  useEffect(() => {
    let cancelled = false;
    setLoadingUsers(true);
    setLoadingConversations(true);
    const fetchUsers = fetch(`${API_BASE}/api/chat/company-users`, { headers: getAuthHeaders() }).then(r => r.json());
    const fetchConvs = fetch(`${API_BASE}/api/chat/conversations`, { headers: getAuthHeaders() }).then(r => r.json());
    Promise.all([fetchUsers, fetchConvs]).then(([userData, convData]) => {
      if (!cancelled && userData?.success && userData.users) setCompanyUsers(userData.users);
      if (!cancelled && convData?.success && convData.conversations) setConversations(convData.conversations);
    }).catch(() => {
      if (!cancelled) setCompanyUsers([]);
      if (!cancelled) setConversations([]);
    }).finally(() => {
      if (!cancelled) {
        setLoadingUsers(false);
        setLoadingConversations(false);
      }
    });
    return () => { cancelled = true; };
  }, []);

  // Refetch conversations when opening a new conversation (e.g. after creating one)
  useEffect(() => {
    if (selectedConversationId == null) return;
    let cancelled = false;
    setLoadingConversations(true);
    fetch(`${API_BASE}/api/chat/conversations`, { headers: getAuthHeaders() })
      .then(r => r.json())
      .then(data => {
        if (!cancelled && data.success && data.conversations) setConversations(data.conversations);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoadingConversations(false); });
    return () => { cancelled = true; };
  }, [selectedConversationId]);

  // Load messages and mark as read when conversation selected
  useEffect(() => {
    if (!selectedConversationId) {
      setMessages([]);
      return;
    }
    let cancelled = false;
    setLoadingMessages(true);
    fetch(`${API_BASE}/api/chat/conversations/${selectedConversationId}/messages?limit=80`, { headers: getAuthHeaders() })
      .then(r => r.json())
      .then(data => {
        if (!cancelled && data.success && data.messages) {
          setMessages(data.messages);
          setHasMoreMessages((data.messages.length || 0) >= 80);
        }
      })
      .catch(() => { if (!cancelled) setMessages([]); })
      .finally(() => { if (!cancelled) setLoadingMessages(false); });
    (async () => {
      fetch(`${API_BASE}/api/chat/conversations/${selectedConversationId}/read`, { method: 'POST', headers: getAuthHeaders() })
        .then(r => r.json())
        .then(d => {
          if (!cancelled && d.success) {
            setConversations(prev => prev.map(c => c.id === selectedConversationId ? { ...c, unreadCount: 0 } : c));
            window.dispatchEvent(new CustomEvent('chat-unread-refresh'));
          }
        })
        .catch(() => {});
    })();
    return () => { cancelled = true; };
  }, [selectedConversationId]);

  // Socket: connect once, use ref for current conversation so real-time works without refresh
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    const s = io(API_BASE, {
      path: '/socket.io',
      auth: { token },
      transports: ['websocket', 'polling']
    });
    s.on('connect', () => {});
    s.on('message:new', (msg) => {
      const currentId = selectedConversationIdRef.current;
      if (msg.conversationId === currentId) {
        setMessages(prev => {
          if (prev.some(m => m.id === msg.id)) return prev;
          return [...prev, { id: msg.id, senderId: msg.senderId, content: msg.content, createdAt: msg.createdAt, senderName: msg.senderName, attachmentPath: msg.attachmentPath, attachmentName: msg.attachmentName, readCount: 0, reactions: {}, myReaction: null }];
        });
      } else {
        window.dispatchEvent(new CustomEvent('chat-unread-refresh'));
        setConversations(prev => {
          const idx = prev.findIndex(c => c.id === msg.conversationId);
          if (idx >= 0) {
            const list = [...prev];
            const c = { ...list[idx], lastMessage: msg.content || (msg.attachmentName ? `📎 ${msg.attachmentName}` : ''), lastMessageAt: msg.createdAt, lastSenderId: msg.senderId, unreadCount: (list[idx].unreadCount || 0) + 1 };
            list.splice(idx, 1);
            return [c, ...list];
          }
          return prev;
        });
        if (conversationsRef.current.findIndex(c => c.id === msg.conversationId) < 0) {
          fetch(`${API_BASE}/api/chat/conversations`, { headers: getAuthHeaders() })
            .then(r => r.json())
            .then(d => { if (d.success && d.conversations) setConversations(d.conversations); })
            .catch(() => {});
        }
      }
    });
    s.on('typing:start', (payload) => {
      if (payload.conversationId === selectedConversationIdRef.current) setTypingUser(payload.userId);
    });
    s.on('typing:stop', (payload) => {
      if (payload.conversationId === selectedConversationIdRef.current) setTypingUser(null);
    });
    s.on('conversation:read', (payload) => {
      const currentId = selectedConversationIdRef.current;
      if (payload.conversationId !== currentId || !payload.messageIds?.length) return;
      setMessages(prev => prev.map(m => payload.messageIds.includes(m.id) ? { ...m, readCount: (m.readCount || 0) + 1 } : m));
    });
    s.on('message:reaction', (payload) => {
      if (payload?.conversationId !== selectedConversationIdRef.current) return;
      setMessages(prev => prev.map(m => m.id === payload.messageId ? { ...m, reactions: payload.reactions || {}, myReaction: payload.myReaction ?? null } : m));
    });
    s.on('conversation:members:updated', (payload) => {
      const convId = payload?.conversationId;
      if (!convId || convId !== selectedConversationIdRef.current) return;
      fetch(`${API_BASE}/api/chat/conversations/${convId}/participants`, { headers: getAuthHeaders() })
        .then(r => r.json())
        .then(d => {
          if (d.success && d.participants) {
            setEditGroupParticipants(d.participants);
            setEditGroupParticipantIds(d.participants.map(p => p.userId));
          }
        })
        .catch(() => {});
      fetch(`${API_BASE}/api/chat/conversations`, { headers: getAuthHeaders() })
        .then(r => r.json())
        .then(d => { if (d.success && d.conversations) setConversations(d.conversations); })
        .catch(() => {});
    });
    s.on('online:list', (ids) => setOnlineUserIds(Array.isArray(ids) ? ids : []));
    s.on('user:status', (payload) => {
      const { userId: uid, status } = payload || {};
      if (!uid) return;
      setOnlineUserIds(prev => status === 'online' ? (prev.includes(uid) ? prev : [...prev, uid]) : prev.filter(id => id !== uid));
    });
    setSocket(s);
    return () => { s.disconnect(); setSocket(null); };
  }, []);

  const openOrCreateDirect = async (otherUser) => {
    try {
      const res = await fetch(`${API_BASE}/api/chat/conversations/direct`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ otherUserId: otherUser.userId })
      });
      const data = await res.json();
      if (data.success && data.conversationId) {
        setSelectedConversationId(data.conversationId);
        setConversations(prev => {
          const exists = prev.some(c => c.id === data.conversationId);
          if (exists) return prev;
          return [{ id: data.conversationId, type: 'direct', peers: [{ userId: otherUser.userId, fullName: otherUser.fullName }] }, ...prev];
        });
      }
    } catch (e) {
      console.error('Open conversation failed', e);
    }
  };

  const openConversation = (conv) => {
    setSelectedConversationId(conv.id);
    setConversations(prev => prev.map(c => c.id === conv.id ? { ...c, unreadCount: 0 } : c));
    window.dispatchEvent(new CustomEvent('chat-unread-refresh'));
  };

  const createGroup = async () => {
    const name = (groupName || '').trim() || 'Group';
    if (groupMemberIds.length === 0) return;
    setCreatingGroup(true);
    try {
      const res = await fetch(`${API_BASE}/api/chat/conversations/group`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ name, memberUserIds: groupMemberIds })
      });
      const data = await res.json();
      if (data.success && data.conversationId) {
        setShowGroupModal(false);
        setGroupName('');
        setGroupMemberIds([]);
        setSelectedConversationId(data.conversationId);
        fetch(`${API_BASE}/api/chat/conversations`, { headers: getAuthHeaders() })
          .then(r => r.json())
          .then(d => { if (d.success && d.conversations) setConversations(d.conversations); });
      }
    } catch (e) {
      console.error('Create group failed', e);
    } finally {
      setCreatingGroup(false);
    }
  };

  const insertEmoji = (emoji) => {
    const em = typeof emoji === 'string' ? emoji : (emoji?.emoji || '');
    if (!em) return;
    const ta = inputRef.current;
    if (ta) {
      const start = ta.selectionStart, end = ta.selectionEnd;
      const before = input.substring(0, start), after = input.substring(end);
      setInput(before + em + after);
      setTimeout(() => { ta.selectionStart = ta.selectionEnd = start + em.length; ta.focus(); }, 0);
    } else setInput(prev => prev + em);
    setShowEmojiPicker(false);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file || uploadingFile || !selectedConversationId) return;
    if (file.size > CHAT_FILE_SIZE_LIMIT) {
      setFileError(t('live_chat.file_too_large') || 'File too large. Max 15MB.');
      e.target.value = '';
      return;
    }
    setFileError('');
    setUploadingFile(true);
    (async () => {
      const fd = new FormData();
      fd.append('file', file);
      try {
        const r = await fetch(`${API_BASE}/api/chat/upload`, { method: 'POST', headers: { Authorization: getAuthHeaders().Authorization }, body: fd });
        const d = await r.json();
        if (d.error && d.error.toLowerCase().includes('large')) {
          setFileError(d.error);
          setUploadingFile(false);
          return;
        }
        if (d.success && d.attachmentPath) {
          const r2 = await fetch(`${API_BASE}/api/chat/messages`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({ conversationId: selectedConversationId, content: '', attachmentPath: d.attachmentPath, attachmentName: d.attachmentName })
          });
          const d2 = await r2.json();
          if (d2.success && d2.message) {
            const msg = { ...d2.message, reactions: d2.message.reactions || {}, myReaction: d2.message.myReaction ?? null };
            setMessages(prev => (prev.some(m => m.id === msg.id) ? prev : [...prev, msg]));
          }
        } else { setFileError(d.error || (t('live_chat.upload_failed') || 'Upload failed')); }
      } catch (err) {
        setFileError(t('live_chat.upload_failed') || 'Upload failed');
      } finally {
        setUploadingFile(false);
      }
    })();
    e.target.value = '';
  };

  const runChatSearch = useCallback(() => {
    const q = (chatSearchQuery || '').trim();
    if (!q) { setChatSearchResults([]); return; }
    const convId = selectedConversationId || '';
    fetch(`${API_BASE}/api/chat/search?q=${encodeURIComponent(q)}${convId ? `&conversationId=${convId}` : ''}`, { headers: getAuthHeaders() })
      .then(r => r.json())
      .then(d => { if (d.success && d.results) setChatSearchResults(d.results); else setChatSearchResults([]); })
      .catch(() => setChatSearchResults([]));
  }, [chatSearchQuery, selectedConversationId]);

  const highlightSearchInText = (text, query) => {
    if (!text || typeof text !== 'string') return text || '';
    const q = (query || '').trim();
    if (!q) return text;
    const parts = text.split(new RegExp(`(${q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'));
    return parts.map((part, i) =>
      part.toLowerCase() === q.toLowerCase()
        ? React.createElement('mark', { key: i, style: { background: 'rgba(251, 191, 36, 0.5)', color: '#fef3c7', padding: '0 2px', borderRadius: 4 } }, part)
        : part
    );
  };

  const scrollToMessageId = (messageId) => {
    setShowChatSearch(false);
    setTimeout(() => {
      const el = messageRefsMap.current[messageId];
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  };

  const reactToMessage = async (messageId, emoji) => {
    if (!messageId) return;
    const msg = messages.find(m => m.id === messageId);
    const currentEmoji = msg?.myReaction;
    const toSend = currentEmoji === emoji ? '' : emoji;
    setReactionPickerMessageId(null);
    setReactionFullPickerMessageId(null);
    try {
      const res = await fetch(`${API_BASE}/api/chat/messages/${messageId}/react`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ emoji: toSend })
      });
      const data = await res.json();
      if (data.success) {
        setMessages(prev => prev.map(m => m.id === messageId ? { ...m, reactions: data.reactions || {}, myReaction: data.myReaction ?? null } : m));
      }
    } catch (e) {}
  };

  useEffect(() => {
    const t = setTimeout(runChatSearch, 300);
    return () => clearTimeout(t);
  }, [chatSearchQuery, runChatSearch]);

  useEffect(() => {
    messageRefsMap.current = {};
  }, [selectedConversationId]);

  const openEditGroup = () => {
    if (!selectedConv || selectedConv.type !== 'group') return;
    setEditGroupName(selectedConv.name || '');
    fetch(`${API_BASE}/api/chat/conversations/${selectedConversationId}/participants`, { headers: getAuthHeaders() })
      .then(r => r.json())
      .then(d => {
        if (d.success && d.participants) {
          setEditGroupParticipants(d.participants);
          setEditGroupParticipantIds(d.participants.map(p => p.userId));
        }
      })
      .catch(() => {});
    setShowEditGroupModal(true);
  };

  const openViewMembers = () => {
    if (!selectedConv || selectedConv.type !== 'group' || !selectedConversationId) return;
    setViewMembersList([]);
    setShowViewMembersModal(true);
    fetch(`${API_BASE}/api/chat/conversations/${selectedConversationId}/participants`, { headers: getAuthHeaders() })
      .then(r => r.json())
      .then(d => {
        if (d.success && d.participants) setViewMembersList(d.participants);
      })
      .catch(() => {});
  };

  const saveEditGroup = async () => {
    if (!selectedConversationId || selectedConv?.type !== 'group') return;
    const currentParticipantIds = editGroupParticipants.map(p => p.userId);
    const toRemove = currentParticipantIds.filter(id => !editGroupParticipantIds.includes(id));
    const toAdd = editGroupParticipantIds.filter(id => !currentParticipantIds.includes(id));
    try {
      const res = await fetch(`${API_BASE}/api/chat/conversations/${selectedConversationId}`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ name: (editGroupName || '').trim() || selectedConv.name, addMemberIds: toAdd, removeMemberIds: toRemove })
      });
      const data = await res.json();
      if (data.success) {
        setShowEditGroupModal(false);
        fetch(`${API_BASE}/api/chat/conversations`, { headers: getAuthHeaders() })
          .then(r => r.json())
          .then(d => { if (d.success && d.conversations) setConversations(d.conversations); });
      }
    } catch (e) {}
  };

  const leaveGroup = async () => {
    if (!selectedConversationId || selectedConv?.type !== 'group') return;
    if (!window.confirm(t('live_chat.exit_group_confirm') || 'Leave this group? You will stop receiving messages.')) return;
    try {
      const res = await fetch(`${API_BASE}/api/chat/conversations/${selectedConversationId}/leave`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      const data = await res.json();
      if (data.success) {
        setShowEditGroupModal(false);
        setSelectedConversationId(null);
        setMessages([]);
        fetch(`${API_BASE}/api/chat/conversations`, { headers: getAuthHeaders() })
          .then(r => r.json())
          .then(d => { if (d.success && d.conversations) setConversations(d.conversations); });
      }
    } catch (e) {}
  };

  const handleSend = async () => {
    const text = (input || '').trim();
    if (!text || sending || !selectedConversationId) return;
    setSending(true);
    setInput('');
    try {
      const res = await fetch(`${API_BASE}/api/chat/messages`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          conversationId: selectedConversationId,
          content: text
        })
      });
      const data = await res.json();
      if (data.success && data.message) {
        const msg = { ...data.message, reactions: data.message.reactions || {}, myReaction: data.message.myReaction ?? null };
        setMessages(prev => (prev.some(m => m.id === msg.id) ? prev : [...prev, msg]));
      }
      if (!data.success) setInput(text);
    } catch (e) {
      setInput(text);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const loadOlderMessages = () => {
    if (!selectedConversationId || messages.length === 0 || loadingOlder || !hasMoreMessages) return;
    const oldestId = messages[0]?.id;
    if (!oldestId) return;
    setLoadingOlder(true);
    const limit = 50;
    fetch(`${API_BASE}/api/chat/conversations/${selectedConversationId}/messages?limit=${limit}&before=${oldestId}`, { headers: getAuthHeaders() })
      .then(r => r.json())
      .then(data => {
        if (data.success && data.messages && data.messages.length > 0) {
          setMessages(prev => [...data.messages, ...prev]);
          setHasMoreMessages(data.messages.length >= limit);
        } else {
          setHasMoreMessages(false);
        }
      })
      .catch(() => setHasMoreMessages(false))
      .finally(() => setLoadingOlder(false));
  };

  const selectedConv = conversations.find(c => c.id === selectedConversationId);
  const peerName = selectedConv?.type === 'group' ? (selectedConv?.name || t('live_chat.group')) : (selectedConv?.peers?.[0]?.fullName || (t('live_chat.conversation') || 'Conversation'));

  const mentionTargets = selectedConv?.type === 'group' ? (selectedConv?.peers || []) : (selectedConv?.peers ? [selectedConv.peers[0]] : []);
  const inputVal = input || '';
  const atMatch = inputVal.match(/@(\w*)$/);
  const showMention = atMatch && mentionTargets.length > 0;
  const mentionFilter = (atMatch && atMatch[1]) ? atMatch[1].toLowerCase() : '';
  const filteredMentions = showMention ? mentionTargets.filter(p => (p.fullName || '').toLowerCase().includes(mentionFilter)) : [];

  const insertMention = (fullName) => {
    const ta = inputRef.current;
    const beforeAt = inputVal.replace(/@\w*$/, '');
    setInput(beforeAt + '@' + fullName + ' ');
    setTimeout(() => { if (ta) { ta.focus(); ta.selectionStart = ta.selectionEnd = (beforeAt + '@' + fullName + ' ').length; } }, 0);
  };

  const filteredUsers = companyUsers.filter(
    u => !searchContact || [u.fullName, u.email, u.department].some(s => (s || '').toLowerCase().includes(searchContact.toLowerCase()))
  );

  // Only show conversations with recent activity (real-time); include current open chat so it stays in list
  const recentConversations = conversations.filter(c => c.lastMessageAt || c.lastMessage || c.id === selectedConversationId);

  const containerStyle = {
    width: '100%',
    height: '100%',
    minHeight: 0,
    maxHeight: '100%',
    flex: 1,
    background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 50%, #0f1320 100%)',
    color: '#fff',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    display: 'flex',
    flexDirection: 'column',
    paddingBottom: 'env(safe-area-inset-bottom, 0)',
    overflow: 'hidden'
  };

  const headerStyle = {
    padding: narrowScreen ? '16px 14px' : '24px 0 20px',
    background: 'transparent',
    borderBottom: 'none',
    flexShrink: 0
  };

  const sidebarStyle = {
    width: narrowScreen ? '100%' : '320px',
    minWidth: narrowScreen ? 0 : '280px',
    maxWidth: narrowScreen ? '100%' : undefined,
    minHeight: 0,
    borderRight: narrowScreen ? 'none' : '1px solid rgba(255,255,255,0.1)',
    background: 'rgba(0,0,0,0.15)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden'
  };

  const mainStyle = {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    minHeight: 0,
    overflow: 'hidden'
  };

  return (
    <div style={containerStyle}>
      <style>{`
        .live-chat-textarea::-webkit-scrollbar { display: none; }
        .live-chat-textarea { scrollbar-width: none; -ms-overflow-style: none; }
        .live-chat-textarea { resize: none; }
        .live-chat-scroll::-webkit-scrollbar { width: 8px; height: 8px; }
        .live-chat-scroll::-webkit-scrollbar-track { background: #0a0a0f; border-radius: 4px; }
        .live-chat-scroll::-webkit-scrollbar-thumb { background: linear-gradient(180deg, #7c3aed 0%, #6d28d9 100%); border-radius: 4px; }
        .live-chat-scroll::-webkit-scrollbar-thumb:hover { background: #6d28d9; }
        .live-chat-scroll { scrollbar-color: #6d28d9 #0a0a0f; scrollbar-width: thin; }
      `}</style>
      <header style={headerStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6, gap: 16 }}>
          {onNavigate && (
            <button
              type="button"
              onClick={() => onNavigate('home')}
              style={{
                background: 'rgba(255,255,255,0.1)',
                border: '1px solid rgba(255,255,255,0.2)',
                color: '#fff',
                padding: 10,
                borderRadius: 12,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                width: 44,
                height: 44
              }}
              title={t('navigation.home') || 'Home'}
            >
              <Home size={20} />
            </button>
          )}
          <div style={{ fontSize: narrowScreen ? 'clamp(22px, 5vw, 28px)' : 'clamp(28px, 5vw, 40px)', fontWeight: 700, maxWidth: '100%', letterSpacing: '-0.5px', lineHeight: 1.2, flex: 1, display: 'flex', alignItems: 'center', gap: 12 }}>
            <MessageCircle size={narrowScreen ? 28 : 32} style={{ color: 'rgba(255,255,255,0.95)', flexShrink: 0 }} />
            {t('home.live_chat') || t('landing.plan_feature_live_chat') || 'Live Chat'}
          </div>
        </div>
        <p style={{ fontSize: narrowScreen ? 13 : 'clamp(14px, 2vw, 16px)', color: 'rgba(255,255,255,0.7)', margin: '8px 0 0 0', letterSpacing: '0.2px', fontWeight: 500 }}>
          {t('live_chat.subtitle') || 'Real-time messaging with your company'}
        </p>
      </header>

      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', padding: narrowScreen ? '0 12px 12px' : '0 0 20px', width: '100%', overflow: 'hidden' }}>
        <div style={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden', minWidth: 0, borderRadius: narrowScreen ? 12 : 16, border: '1px solid rgba(255,255,255,0.12)', background: 'rgba(0,0,0,0.2)', boxShadow: '0 4px 24px rgba(0,0,0,0.2)' }}>
        {/* Sidebar: contacts (hidden on narrow when conversation open) */}
        <aside style={{
          ...sidebarStyle,
          display: narrowScreen && selectedConversationId ? 'none' : 'flex',
          flexShrink: 0,
          alignSelf: 'stretch'
        }}>
          <div style={{ padding: narrowScreen ? '10px 12px' : '12px 16px', borderBottom: '1px solid rgba(255,255,255,0.08)', flexShrink: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
              <Search size={18} style={{ color: 'rgba(255,255,255,0.5)', flexShrink: 0 }} />
              <input
                type="text"
                placeholder={t('live_chat.search_contacts') || 'Search contacts...'}
                value={searchContact}
                onChange={e => setSearchContact(e.target.value)}
                style={{
                  flex: 1,
                  minWidth: 0,
                  background: 'rgba(255,255,255,0.08)',
                  border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: '10px',
                  padding: narrowScreen ? '12px 14px' : '10px 12px',
                  color: '#fff',
                  fontSize: '14px'
                }}
              />
            </div>
            <button
              type="button"
              onClick={() => setShowGroupModal(true)}
              style={{
                width: '100%',
                padding: narrowScreen ? '14px' : '10px 14px',
                minHeight: 44,
                borderRadius: '10px',
                border: '1px dashed rgba(255,255,255,0.3)',
                background: 'rgba(255,255,255,0.06)',
                color: '#fff',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              <Users size={18} />
              {t('live_chat.new_group') || 'New group'}
            </button>
          </div>
          <div className="live-chat-scroll" style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', WebkitOverflowScrolling: 'touch' }}>
            {loadingConversations || loadingUsers ? (
              <div style={{ padding: '24px', textAlign: 'center', color: 'rgba(255,255,255,0.5)' }}>Loading...</div>
            ) : (
              <>
                {recentConversations.length > 0 && (
                  <>
                    <div style={{ padding: '8px 16px', fontSize: '11px', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      {t('live_chat.recent') || 'Recent'}
                    </div>
                    {recentConversations.map(conv => {
                      const title = conv.type === 'group' ? (conv.name || t('live_chat.group')) : (conv.peers?.[0]?.fullName || '—');
                      const isSelected = conv.id === selectedConversationId;
                      const peerUserId = conv.type === 'direct' ? conv.peers?.[0]?.userId : null;
                      const isOnline = peerUserId && onlineUserIds.includes(peerUserId);
                      return (
                        <button
                          key={conv.id}
                          type="button"
                          onClick={() => openConversation(conv)}
                          style={{
                            width: '100%',
                            padding: narrowScreen ? '14px 16px' : '12px 16px',
                            minHeight: 56,
                            border: 'none',
                            background: isSelected ? 'rgba(56, 189, 248, 0.25)' : 'transparent',
                            color: '#fff',
                            textAlign: 'left',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            borderBottom: '1px solid rgba(255,255,255,0.05)'
                          }}
                        >
                          <div style={{
                            width: '42px',
                            height: '42px',
                            borderRadius: conv.type === 'group' ? '10px' : '50%',
                            background: conv.type === 'group' ? 'linear-gradient(135deg, #8b5cf6, #a78bfa)' : 'linear-gradient(135deg, #0ea5e9, #38bdf8)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 600,
                            fontSize: '15px',
                            flexShrink: 0
                          }}>
                            {conv.type === 'group' ? <Users size={20} /> : (title || '?').charAt(0).toUpperCase()}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 600, fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                              {conv.type === 'direct' && (
                                <span style={{ width: 8, height: 8, borderRadius: 4, flexShrink: 0, background: isOnline ? '#22c55e' : 'rgba(255,255,255,0.35)' }} title={isOnline ? (t('live_chat.online') || 'Online') : (t('live_chat.offline') || 'Offline')} />
                              )}
                              {title}
                              {(conv.unreadCount || 0) > 0 && (
                                <span style={{
                                  background: '#ef4444',
                                  color: '#fff',
                                  fontSize: '11px',
                                  fontWeight: 700,
                                  minWidth: '18px',
                                  height: '18px',
                                  borderRadius: 9,
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  padding: '0 4px'
                                }}>
                                  {conv.unreadCount > 99 ? '99+' : conv.unreadCount}
                                </span>
                              )}
                            </div>
                            {conv.lastMessage && (
                              <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {conv.lastMessage}
                              </div>
                            )}
                          </div>
                          <ChevronRight size={16} style={{ color: 'rgba(255,255,255,0.4)', flexShrink: 0 }} />
                        </button>
                      );
                    })}
                  </>
                )}
                {recentConversations.length === 0 && (
                  <div style={{ padding: '20px 16px', textAlign: 'center', color: 'rgba(255,255,255,0.5)', fontSize: '13px' }}>
                    {t('live_chat.no_recent_chats') || 'No recent chats'}
                  </div>
                )}
                <div style={{ padding: '8px 16px', fontSize: '11px', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: recentConversations.length ? '8px' : 0 }}>
                  {t('live_chat.company_contacts') || 'Company contacts'}
                </div>
                {filteredUsers.map(user => {
                  const userOnline = onlineUserIds.includes(user.userId);
                  return (
                  <button
                    key={user.userId}
                    type="button"
                    onClick={() => openOrCreateDirect(user)}
                    style={{
                      width: '100%',
                      padding: narrowScreen ? '14px 16px' : '12px 16px',
                      minHeight: 56,
                      border: 'none',
                      background: selectedConversationId && selectedConv?.type === 'direct' && selectedConv?.peers?.[0]?.userId === user.userId
                        ? 'rgba(56, 189, 248, 0.2)'
                        : 'transparent',
                      color: '#fff',
                      textAlign: 'left',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      borderBottom: '1px solid rgba(255,255,255,0.05)'
                    }}
                  >
                    <div style={{
                      width: '42px',
                      height: '42px',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #0ea5e9, #38bdf8)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 600,
                      fontSize: '16px',
                      flexShrink: 0
                    }}>
                      {(user.firstName || user.fullName || '?').charAt(0).toUpperCase()}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ width: 8, height: 8, borderRadius: 4, flexShrink: 0, background: userOnline ? '#22c55e' : 'rgba(255,255,255,0.35)' }} title={userOnline ? (t('live_chat.online') || 'Online') : (t('live_chat.offline') || 'Offline')} />
                        {user.fullName || user.email}
                      </div>
                      {user.department && (
                        <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>{user.department}</div>
                      )}
                    </div>
                    <ChevronRight size={16} style={{ color: 'rgba(255,255,255,0.4)', flexShrink: 0 }} />
                  </button>
                  );
                })}
                {filteredUsers.length === 0 && (
                  <div style={{ padding: '16px', textAlign: 'center', color: 'rgba(255,255,255,0.5)', fontSize: '13px' }}>
                    {t('live_chat.no_contacts') || 'No other contacts in your company.'}
                  </div>
                )}
              </>
            )}
          </div>
        </aside>

        {/* Group create modal */}
        {showGroupModal && (
          <div style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: 20
          }} onClick={() => !creatingGroup && setShowGroupModal(false)}>
            <div style={{
              background: 'linear-gradient(180deg, #1a1f3a 0%, #0f1320 100%)',
              borderRadius: '16px',
              border: '1px solid rgba(255,255,255,0.15)',
              maxWidth: 420,
              width: '100%',
              maxHeight: '85vh',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 20px 60px rgba(0,0,0,0.5)'
            }} onClick={e => e.stopPropagation()}>
              <div style={{ padding: '20px 20px 16px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>{t('live_chat.new_group') || 'New group'}</h3>
                <input
                  type="text"
                  placeholder={t('live_chat.group_name') || 'Group name'}
                  value={groupName}
                  onChange={e => setGroupName(e.target.value)}
                  style={{
                    width: '100%',
                    marginTop: '12px',
                    padding: '12px 14px',
                    borderRadius: '10px',
                    border: '1px solid rgba(255,255,255,0.2)',
                    background: 'rgba(255,255,255,0.08)',
                    color: '#fff',
                    fontSize: '15px'
                  }}
                />
              </div>
              <div style={{ padding: '12px 20px', fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>
                {t('live_chat.select_members') || 'Select members'}
              </div>
              <div style={{ flex: 1, overflowY: 'auto', padding: '0 20px 16px' }}>
                {companyUsers.map(u => (
                  <label key={u.userId} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '10px 0',
                    cursor: 'pointer',
                    borderBottom: '1px solid rgba(255,255,255,0.06)'
                  }}>
                    <input
                      type="checkbox"
                      checked={groupMemberIds.includes(u.userId)}
                      onChange={e => setGroupMemberIds(prev => e.target.checked ? [...prev, u.userId] : prev.filter(id => id !== u.userId))}
                    />
                    <span style={{ fontWeight: 500 }}>{u.fullName || u.email}</span>
                  </label>
                ))}
              </div>
              <div style={{ padding: '16px 20px', display: 'flex', gap: '10px', justifyContent: 'flex-end', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                <button type="button" onClick={() => !creatingGroup && setShowGroupModal(false)} style={{ padding: '10px 20px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.3)', background: 'transparent', color: '#fff', cursor: 'pointer' }}>
                  {t('common.cancel') || 'Cancel'}
                </button>
                <button type="button" onClick={createGroup} disabled={creatingGroup || groupMemberIds.length === 0} style={{
                  padding: '10px 20px', borderRadius: '10px', border: 'none', background: groupMemberIds.length ? 'linear-gradient(135deg, #3b82f6, #2563eb)' : 'rgba(255,255,255,0.2)', color: '#fff', cursor: groupMemberIds.length ? 'pointer' : 'not-allowed', fontWeight: 600
                }}>
                  {creatingGroup ? (t('common.creating') || 'Creating...') : (t('live_chat.create_group') || 'Create')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* View group members modal (non-admin: read-only list) */}
        {showViewMembersModal && selectedConv?.type === 'group' && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }} onClick={() => setShowViewMembersModal(false)}>
            <div style={{ background: 'linear-gradient(180deg, #1a1f3a 0%, #0f1320 100%)', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.15)', maxWidth: 380, width: '100%', maxHeight: '80vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }} onClick={e => e.stopPropagation()}>
              <div style={{ padding: '20px 20px 12px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>{t('live_chat.view_members') || 'View members'}</h3>
              </div>
              <div style={{ flex: 1, overflowY: 'auto', padding: '12px 20px 20px' }}>
                {viewMembersList.length === 0 ? (
                  <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px' }}>{t('live_chat.loading') || 'Loading...'}</div>
                ) : (
                  viewMembersList.map(p => (
                    <div key={p.userId} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                      <span style={{ fontWeight: 500 }}>{p.fullName}</span>
                      {p.role === 'admin' && <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>({t('live_chat.admin') || 'Admin'})</span>}
                    </div>
                  ))
                )}
              </div>
              <div style={{ padding: '16px 20px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                <button type="button" onClick={() => setShowViewMembersModal(false)} style={{ width: '100%', padding: '10px 20px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.3)', background: 'transparent', color: '#fff', cursor: 'pointer', fontWeight: 500 }}>{t('common.close') || 'Close'}</button>
              </div>
            </div>
          </div>
        )}

        {/* Edit group modal */}
        {showEditGroupModal && selectedConv?.type === 'group' && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }} onClick={() => setShowEditGroupModal(false)}>
            <div style={{ background: 'linear-gradient(180deg, #1a1f3a 0%, #0f1320 100%)', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.15)', maxWidth: 420, width: '100%', maxHeight: '85vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }} onClick={e => e.stopPropagation()}>
              <div style={{ padding: '20px 20px 16px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>{t('live_chat.edit_group') || 'Edit group'}</h3>
                <input type="text" placeholder={t('live_chat.group_name') || 'Group name'} value={editGroupName} onChange={e => setEditGroupName(e.target.value)} style={{ width: '100%', marginTop: '12px', padding: '12px 14px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.08)', color: '#fff', fontSize: '15px' }} />
              </div>
              <div style={{ padding: '12px 20px', fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>{t('live_chat.members') || 'Members'}</div>
              <div style={{ flex: 1, overflowY: 'auto', padding: '0 20px 16px' }}>
                {editGroupParticipants.map(p => (
                  <label key={p.userId} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 0', cursor: 'pointer', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                    <input type="checkbox" checked={editGroupParticipantIds.includes(p.userId)} onChange={e => setEditGroupParticipantIds(prev => e.target.checked ? [...prev, p.userId] : prev.filter(id => id !== p.userId))} />
                    <span style={{ fontWeight: 500 }}>{p.fullName} {p.role === 'admin' ? '(Admin)' : ''}</span>
                  </label>
                ))}
                <div style={{ marginTop: '12px', fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>{t('live_chat.add_members') || 'Add members'}</div>
                {companyUsers.filter(u => !editGroupParticipantIds.includes(u.userId)).map(u => (
                  <button key={u.userId} type="button" onClick={() => setEditGroupParticipantIds(prev => [...prev, u.userId])} style={{ width: '100%', padding: '10px 0', textAlign: 'left', background: 'none', border: 'none', color: '#93c5fd', cursor: 'pointer', fontSize: '14px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                    + {u.fullName || u.email}
                  </button>
                ))}
              </div>
              <div style={{ padding: '16px 20px', display: 'flex', gap: '10px', justifyContent: 'flex-end', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                <button type="button" onClick={() => setShowEditGroupModal(false)} style={{ padding: '10px 20px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.3)', background: 'transparent', color: '#fff', cursor: 'pointer' }}>{t('common.cancel') || 'Cancel'}</button>
                <button type="button" onClick={saveEditGroup} style={{ padding: '10px 20px', borderRadius: '10px', border: 'none', background: 'linear-gradient(135deg, #3b82f6, #2563eb)', color: '#fff', cursor: 'pointer', fontWeight: 600 }}>{t('common.save') || 'Save'}</button>
              </div>
            </div>
          </div>
        )}

        {/* Main: conversation or empty state (on narrow screen hide when no conversation) */}
        <main style={{
          ...mainStyle,
          display: narrowScreen && !selectedConversationId ? 'none' : 'flex',
          minHeight: 0,
          alignSelf: 'stretch'
        }}>
          {!selectedConversationId ? (
            <div style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '40px',
              color: 'rgba(255,255,255,0.6)',
              textAlign: 'center'
            }}>
              <MessageCircle size={64} style={{ marginBottom: '16px', opacity: 0.4 }} />
              <p style={{ fontSize: '16px', margin: 0 }}>
                {t('live_chat.select_contact') || 'Select a contact from the list to start chatting.'}
              </p>
              <p style={{ fontSize: '14px', margin: '8px 0 0' }}>
                {t('live_chat.company_only') || 'Only employees from your company are shown.'}
              </p>
            </div>
          ) : (
            <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              {/* Chat header bar: responsive on mobile — row 1 = back + avatar + name; row 2 (mobile) = search + actions */}
              <div style={{
                flexShrink: 0,
                padding: narrowScreen ? '10px 12px' : '14px 20px',
                borderBottom: '1px solid rgba(255,255,255,0.1)',
                background: 'rgba(255,255,255,0.04)',
                display: 'flex',
                flexDirection: narrowScreen ? 'column' : 'row',
                alignItems: 'stretch',
                gap: narrowScreen ? '10px' : '12px',
                flexWrap: 'wrap'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: narrowScreen ? '8px' : '12px', flex: narrowScreen ? '0 0 auto' : '1', minWidth: 0 }}>
                  {narrowScreen && (
                    <button type="button" onClick={() => setSelectedConversationId(null)} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff', padding: '8px', borderRadius: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }} title={t('common.back') || 'Back'}>
                      <ArrowLeft size={20} />
                    </button>
                  )}
                  <div style={{ width: '40px', height: '40px', borderRadius: selectedConv?.type === 'group' ? '10px' : '50%', background: selectedConv?.type === 'group' ? 'linear-gradient(135deg, #8b5cf6, #a78bfa)' : 'linear-gradient(135deg, #0ea5e9, #38bdf8)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600, fontSize: '15px', flexShrink: 0 }}>
                    {selectedConv?.type === 'group' ? <Users size={20} /> : (peerName || '?').charAt(0).toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                    <div style={{ fontWeight: 600, fontSize: narrowScreen ? '14px' : '15px', display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0, overflow: 'hidden' }}>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={peerName}>{peerName}</span>
                      {selectedConv?.type === 'direct' && (() => {
                        const pid = selectedConv?.peers?.[0]?.userId;
                        const onl = pid && onlineUserIds.includes(pid);
                        return (
                          <span style={{ fontSize: '11px', fontWeight: 500, color: onl ? '#22c55e' : 'rgba(255,255,255,0.5)', flexShrink: 0 }}>
                            {onl ? (t('live_chat.online') || 'Online') : (t('live_chat.offline') || 'Offline')}
                          </span>
                        );
                      })()}
                    </div>
                    <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)', minHeight: '18px', fontStyle: typingUser ? 'italic' : 'normal' }}>
                      {typingUser ? (t('live_chat.typing') || 'typing...') : '\u00A0'}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0, ...(narrowScreen ? { flex: '1', minWidth: 0 } : {}) }}>
                  <input
                    type="text"
                    placeholder={t('live_chat.search_in_chat') || 'Search in chat'}
                    value={chatSearchQuery}
                    onChange={e => setChatSearchQuery(e.target.value)}
                    onFocus={() => setShowChatSearch(true)}
                    style={{
                      width: narrowScreen ? '100%' : 140,
                      minWidth: narrowScreen ? 0 : 100,
                      flex: narrowScreen ? 1 : undefined,
                      padding: narrowScreen ? '8px 10px' : '8px 12px',
                      borderRadius: '8px',
                      border: '1px solid rgba(255,255,255,0.2)',
                      background: 'rgba(255,255,255,0.08)',
                      color: '#fff',
                      fontSize: narrowScreen ? '13px' : '13px'
                    }}
                  />
                  {selectedConv?.type === 'group' && selectedConv?.myRole === 'admin' && (
                    <button type="button" onClick={openEditGroup} style={{ padding: narrowScreen ? '6px' : '8px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.08)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }} title={t('live_chat.edit_group') || 'Edit group'}>
                      <Pencil size={narrowScreen ? 16 : 18} />
                    </button>
                  )}
                  {selectedConv?.type === 'group' && selectedConv?.myRole !== 'admin' && (
                    <button type="button" onClick={openViewMembers} style={{ padding: narrowScreen ? '6px' : '8px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.08)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }} title={t('live_chat.view_members') || 'View members'}>
                      <Eye size={narrowScreen ? 16 : 18} />
                    </button>
                  )}
                  {selectedConv?.type === 'group' && (
                    <button type="button" onClick={leaveGroup} style={{ padding: narrowScreen ? '6px' : '8px', borderRadius: '8px', border: '1px solid rgba(239,68,68,0.4)', background: 'rgba(239,68,68,0.15)', color: '#fca5a5', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }} title={t('live_chat.exit_group') || 'Exit group'}>
                      <LogOut size={narrowScreen ? 16 : 18} />
                    </button>
                  )}
                </div>
              </div>
              {showChatSearch && (chatSearchQuery.trim() || chatSearchResults.length > 0) && (
                <div style={{ flexShrink: 0, padding: '12px 20px', background: 'rgba(0,0,0,0.2)', borderBottom: '1px solid rgba(255,255,255,0.1)', maxHeight: 220, overflowY: 'auto' }}>
                  <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)', marginBottom: '8px' }}>{t('live_chat.search_results') || 'Search results'}</div>
                  {chatSearchResults.length === 0 ? (chatSearchQuery.trim() && <div style={{ color: 'rgba(255,255,255,0.5)' }}>{t('live_chat.no_results') || 'No results'}</div>) : chatSearchResults.map(r => (
                    <button
                      key={r.id}
                      type="button"
                      onClick={() => scrollToMessageId(r.id)}
                      style={{
                        width: '100%',
                        padding: '10px 0',
                        border: 'none',
                        borderBottom: '1px solid rgba(255,255,255,0.06)',
                        background: 'transparent',
                        color: 'inherit',
                        fontSize: '13px',
                        textAlign: 'left',
                        cursor: 'pointer',
                        transition: 'background 0.15s'
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                    >
                      <div style={{ color: 'rgba(255,255,255,0.7)', marginBottom: '4px' }}>{r.senderName} · {r.conversationName ? `${r.conversationName} · ` : ''}{new Date(r.createdAt).toLocaleString()}</div>
                      <div style={{ marginTop: '4px', color: 'rgba(255,255,255,0.9)' }}>
                        {highlightSearchInText(r.content || (r.attachmentName ? `📎 ${r.attachmentName}` : ''), chatSearchQuery)}
                      </div>
                    </button>
                  ))}
                </div>
              )}

              <p style={{ flexShrink: 0, margin: 0, padding: '8px 20px', fontSize: '12px', color: 'rgba(255,255,255,0.5)', textAlign: 'center', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                {t('live_chat.messages_auto_delete_notice') || 'Messages older than 30 days will disappear automatically.'}
              </p>

              <div
                ref={messagesScrollRef}
                className="live-chat-scroll"
                onScroll={() => { setReactionPickerMessageId(null); setReactionFullPickerMessageId(null); }}
                style={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  padding: narrowScreen ? '12px' : '20px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '10px'
                }}
              >
                {loadingMessages ? (
                  <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.5)', padding: '24px' }}>Loading messages...</div>
                ) : (
                  <>
                    {hasMoreMessages && (
                      <div ref={messagesTopRef} style={{ display: 'flex', justifyContent: 'center', padding: '12px 0' }}>
                        <button type="button" onClick={loadOlderMessages} disabled={loadingOlder} style={{ padding: '8px 16px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.25)', background: 'rgba(255,255,255,0.08)', color: '#93c5fd', fontSize: '13px', cursor: loadingOlder ? 'not-allowed' : 'pointer' }}>
                          {loadingOlder ? (t('live_chat.loading_older') || 'Loading...') : (t('live_chat.load_older_messages') || 'Load older messages')}
                        </button>
                      </div>
                    )}
                    {messages.map(msg => {
                      const isOwnMsg = !selectedConv?.peers?.some(p => p.userId === msg.senderId);
                      return (
                        <div
                          key={msg.id}
                          ref={el => { if (el) messageRefsMap.current[msg.id] = el; }}
                          data-message-id={msg.id}
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignSelf: isOwnMsg ? 'flex-end' : 'flex-start',
                            alignItems: isOwnMsg ? 'flex-end' : 'flex-start',
                            maxWidth: narrowScreen ? '92%' : '80%'
                          }}
                        >
                          {/* Message bubble: content + timestamp only */}
                          <div
                            style={{
                              padding: narrowScreen ? '10px 14px' : '12px 16px',
                              borderRadius: '16px',
                              background: isOwnMsg
                                ? 'linear-gradient(135deg, #3b82f6, #2563eb)'
                                : 'rgba(255,255,255,0.1)',
                              border: isOwnMsg ? 'none' : '1px solid rgba(255,255,255,0.15)'
                            }}
                          >
                            {!isOwnMsg && (
                              <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.7)', marginBottom: '4px' }}>
                                {msg.senderName}
                              </div>
                            )}
                            {msg.content && (
                              <div style={{ fontSize: '15px', lineHeight: 1.4, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                {msg.content}
                              </div>
                            )}
                            {(msg.attachmentPath || msg.attachmentName) && (() => {
                              const name = msg.attachmentName || 'File';
                              const isImage = isImageFileName(name);
                              const handleFileError = (res) => {
                                const msg404 = t('live_chat.file_not_found') || 'File not found on server. It may have been removed.';
                                const msgOther = t('live_chat.download_failed') || 'Could not load file. Stay on this page and try again.';
                                alert(res?.status === 404 ? msg404 : msgOther);
                              };
                              const viewFile = async () => {
                                try {
                                  const url = getDownloadUrl(msg.attachmentPath, name, 'inline');
                                  const res = await fetch(url, { headers: getAuthHeaders() });
                                  if (!res.ok) { handleFileError(res); return; }
                                  const blob = await res.blob();
                                  const blobUrl = URL.createObjectURL(blob);
                                  window.open(blobUrl, '_blank');
                                  setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
                                } catch {
                                  alert(t('live_chat.download_failed') || 'Could not load file. Stay on this page and try again.');
                                }
                              };
                              const downloadFile = async () => {
                                try {
                                  const url = getDownloadUrl(msg.attachmentPath, name);
                                  const res = await fetch(url, { headers: getAuthHeaders() });
                                  if (!res.ok) { handleFileError(res); return; }
                                  const blob = await res.blob();
                                  const blobUrl = URL.createObjectURL(blob);
                                  const a = document.createElement('a');
                                  a.href = blobUrl;
                                  a.download = name;
                                  a.click();
                                  URL.revokeObjectURL(blobUrl);
                                } catch {
                                  alert(t('live_chat.download_failed') || 'Download failed. Stay on this page and try again.');
                                }
                              };
                              return (
                                <div style={{ marginTop: '6px' }}>
                                  {isImage && msg.attachmentPath && (
                                    <div style={{ marginBottom: '8px' }}>
                                      <ChatAttachmentImage path={msg.attachmentPath} name={name} alt={name} />
                                    </div>
                                  )}
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', color: 'rgba(255,255,255,0.9)', fontSize: '14px', minWidth: 0 }}>
                                      {!isImage && <FileText size={18} style={{ flexShrink: 0, opacity: 0.9 }} />}
                                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={name}>{name}</span>
                                    </span>
                                    {msg.attachmentPath && (
                                      <>
                                        <button type="button" onClick={viewFile} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36, background: 'rgba(255,255,255,0.12)', borderRadius: '8px', color: '#93c5fd', border: 'none', cursor: 'pointer', flexShrink: 0 }} title={t('live_chat.preview') || 'Preview'}>
                                          <Eye size={18} />
                                        </button>
                                        <button type="button" onClick={downloadFile} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36, background: 'rgba(255,255,255,0.15)', borderRadius: '8px', color: '#93c5fd', border: 'none', cursor: 'pointer', flexShrink: 0 }} title={t('live_chat.download') || 'Download'}>
                                          <Download size={18} />
                                        </button>
                                      </>
                                    )}
                                  </div>
                                </div>
                              );
                            })()}
                            <div style={{ fontSize: '11px', opacity: 0.8, marginTop: '4px', display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'flex-end', flexWrap: 'wrap', minWidth: 0 }}>
                              {msg.createdAt ? new Date(msg.createdAt).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }) : ''}
                              {isOwnMsg && (
                                <span style={{ fontSize: '10px', opacity: 0.9 }}>
                                  {(msg.readCount || 0) > 0 ? (t('live_chat.read') || 'Read') : (t('live_chat.sent') || 'Sent')}
                                </span>
                              )}
                            </div>
                          </div>
                          {/* Reactions row: below bubble (WhatsApp-style), picker opens right for own / left-anchored for others */}
                          <div style={{ position: 'relative', display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center', marginTop: '2px', minWidth: 0, justifyContent: isOwnMsg ? 'flex-end' : 'flex-start' }}>
                            {(msg.reactions && Object.keys(msg.reactions).length > 0) && (
                              <>
                                {Object.entries(msg.reactions).map(([emoji, count]) => (
                                  <button
                                    key={emoji}
                                    type="button"
                                    onClick={() => reactToMessage(msg.id, emoji)}
                                    style={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: '3px',
                                      padding: '4px 8px',
                                      minWidth: 'min-content',
                                      flexShrink: 0,
                                      borderRadius: '12px',
                                      border: '1px solid rgba(255,255,255,0.2)',
                                      background: msg.myReaction === emoji ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
                                      color: '#fff',
                                      fontSize: narrowScreen ? '14px' : '13px',
                                      cursor: 'pointer'
                                    }}
                                    title={t('live_chat.react') || 'React'}
                                  >
                                    <span>{emoji}</span>
                                    {count > 1 && <span style={{ fontSize: '11px', opacity: 0.9 }}>{count}</span>}
                                  </button>
                                ))}
                              </>
                            )}
                            <button
                              type="button"
                              data-reaction-trigger
                              onClick={() => { setReactionFullPickerMessageId(null); setReactionPickerMessageId(prev => prev === msg.id ? null : msg.id); }}
                              style={{ background: 'none', border: 'none', padding: '4px', cursor: 'pointer', color: 'rgba(255,255,255,0.6)', display: 'flex', flexShrink: 0 }}
                              title={t('live_chat.add_reaction') || 'Add reaction'}
                            >
                              <SmilePlus size={narrowScreen ? 18 : 14} />
                            </button>
                            {reactionPickerMessageId === msg.id && (
                              <div data-reaction-picker style={{
                                position: 'absolute',
                                bottom: '100%',
                                ...(isOwnMsg ? { right: 0 } : { left: 0 }),
                                marginBottom: '4px',
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '6px',
                                padding: '8px',
                                background: 'rgba(0,0,0,0.9)',
                                borderRadius: '12px',
                                border: '1px solid rgba(255,255,255,0.15)',
                                boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
                                zIndex: 10,
                                maxWidth: narrowScreen ? 'calc(100vw - 24px)' : 320
                              }}>
                                {QUICK_REACTIONS.map(emoji => (
                                  <button key={emoji} type="button" onClick={() => reactToMessage(msg.id, emoji)} style={{ padding: '6px', fontSize: narrowScreen ? '22px' : '18px', background: 'none', border: 'none', cursor: 'pointer', flexShrink: 0 }}>{emoji}</button>
                                ))}
                                <button
                                  type="button"
                                  onClick={() => { setReactionPickerMessageId(null); setReactionFullPickerMessageId(prev => prev === msg.id ? null : msg.id); }}
                                  style={{ padding: '4px 8px', fontSize: '16px', fontWeight: 700, background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px', color: '#fff', cursor: 'pointer', flexShrink: 0 }}
                                  title={t('live_chat.more_emoji') || 'More emoji'}
                                >
                                  +
                                </button>
                              </div>
                            )}
                            {reactionFullPickerMessageId === msg.id && (
                              <div data-reaction-picker style={{
                                position: 'absolute',
                                bottom: '100%',
                                ...(isOwnMsg ? { right: 0 } : { left: 0 }),
                                marginBottom: '4px',
                                zIndex: 11,
                                borderRadius: '12px',
                                overflow: 'hidden',
                                border: '1px solid rgba(255,255,255,0.15)',
                                boxShadow: '0 4px 20px rgba(0,0,0,0.5)'
                              }}>
                                <EmojiPicker onEmojiClick={(e) => reactToMessage(msg.id, e.emoji)} theme="dark" width={narrowScreen ? 280 : 320} height={360} />
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </>
                )}
              </div>

              <div style={{
                flexShrink: 0,
                padding: narrowScreen ? '10px 14px' : '12px 20px',
                paddingBottom: `calc(${narrowScreen ? 10 : 12}px + env(safe-area-inset-bottom, 0))`,
                background: 'rgba(0,0,0,0.2)',
                borderTop: '1px solid rgba(255,255,255,0.1)'
              }}>
                {showEmojiPicker && (
                  <div style={{ marginBottom: '8px' }}>
                    <EmojiPicker onEmojiClick={e => insertEmoji(e.emoji)} theme="dark" width="100%" height={narrowScreen ? 280 : 320} />
                  </div>
                )}
                <input type="file" ref={fileInputRef} style={{ display: 'none' }} onChange={handleFileSelect} />
                {fileError && (
                  <div style={{ padding: '8px 12px', marginBottom: '8px', background: 'rgba(239,68,68,0.2)', borderRadius: '8px', color: '#fca5a5', fontSize: '13px' }}>
                    {fileError}
                  </div>
                )}
                {showMention && filteredMentions.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', padding: '8px 0', marginBottom: '4px', maxHeight: 120, overflowY: 'auto', background: 'rgba(0,0,0,0.2)', borderRadius: '10px' }}>
                    {filteredMentions.slice(0, 5).map(p => (
                      <button key={p.userId} type="button" onClick={() => insertMention(p.fullName)} style={{ padding: '8px 12px', textAlign: 'left', background: 'none', border: 'none', color: '#93c5fd', cursor: 'pointer', fontSize: '14px' }}>
                        @{p.fullName}
                      </button>
                    ))}
                  </div>
                )}
                <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
                  <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploadingFile} style={{ width: '44px', height: '44px', borderRadius: '50%', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.08)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }} title={t('live_chat.attach_file') || 'Attach file'}>
                    <Paperclip size={22} />
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowEmojiPicker(prev => !prev)}
                    style={{
                      width: '44px',
                      height: '44px',
                      borderRadius: '50%',
                      border: '1px solid rgba(255,255,255,0.2)',
                      background: showEmojiPicker ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.08)',
                      color: '#fff',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}
                    title={t('live_chat.emoji') || 'Emoji'}
                  >
                    <Smile size={22} />
                  </button>
                  <textarea
                    ref={inputRef}
                    className="live-chat-textarea"
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onFocus={() => socket && socket.emit('typing:start', { conversationId: selectedConversationId })}
                    onBlur={() => socket && socket.emit('typing:stop', { conversationId: selectedConversationId })}
                    placeholder={t('live_chat.type_message') || 'Type a message...'}
                    rows={1}
                    style={{
                      flex: 1,
                      minHeight: '44px',
                      maxHeight: '120px',
                      padding: '12px 16px',
                      borderRadius: '22px',
                      border: '1px solid rgba(255,255,255,0.2)',
                      background: 'rgba(255,255,255,0.08)',
                      color: '#fff',
                      fontSize: '15px',
                      resize: 'none',
                      overflowY: 'auto',
                      overflowX: 'hidden'
                    }}
                  />
                  <button
                    type="button"
                    onClick={handleSend}
                    disabled={sending || !input.trim()}
                    style={{
                      width: '44px',
                      height: '44px',
                      borderRadius: '50%',
                      border: 'none',
                      background: input.trim() ? 'linear-gradient(135deg, #3b82f6, #2563eb)' : 'rgba(255,255,255,0.15)',
                      color: '#fff',
                      cursor: input.trim() ? 'pointer' : 'not-allowed',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      boxShadow: input.trim() ? '0 2px 8px rgba(59, 130, 246, 0.4)' : 'none'
                    }}
                  >
                    <Send size={20} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </main>
        </div>
      </div>
    </div>
  );
}

export default LiveChat;
